import { DEFAULT_PERMISSIONS } from '@/constants/constants';
import { RegionAndCity } from '@/Interface/regionAndCity';
import { TariffTypeAndCompany, Tariffs } from '@/Interface/tariffs';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PermissionsService } from '@services/permissions.service';
import { UtilityService } from '@services/utility.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2';
export function noWhitespaceOrNumValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    const value = control.value || '';
    const trimmedValue = value.trim();

    // Check for leading whitespace
    const isWhitespace = value.length > 0 && value.trim().length === 0;

    // Check if the value (after trimming leading whitespace) consists only of numbers
    const isOnlyNumbers = /^[0-9]+$/.test(trimmedValue);

    // Combine both validations
    return isWhitespace || isOnlyNumbers ? { 'invalidInput': true } : null;
  };
}
@Component({
  selector: 'app-region-and-city',
  templateUrl: './region-and-city.component.html',
  styleUrls: ['./region-and-city.component.scss']
})
export class RegionAndCityComponent implements OnInit {
  public addCItyForm: FormGroup
  public editCityForm: FormGroup
  public pageSizeControl = new FormControl(10);
  pageSize: number;
  pageToGo: number = 1;
  searchElement = '';
  serialNumberArray: Array<number> = [];
  next_page: number;
  current_page: number;
  previous_page: number;
  total_pages: number;
  total_items: number;
  private latestQueryParams: any = {};
  sortColumn: string = null;
  sortOrder: 'asc' | 'desc' = null;
  searchSubject: Subject<string> = new Subject();
  loader: boolean = false
  permissions = { ...DEFAULT_PERMISSIONS };
  permissionsReady: boolean = false;

  public pageRecordOptions = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
    { value: '200', label: '200' },
  ];

  public citiesList: TariffTypeAndCompany[] = [];
  public selectedId: number
  regionList: any;
  regionLoading: boolean = false;

  constructor(private modalService: NgbModal,
    private _utilityService: UtilityService,
    private toaster: ToastrService,
    private formBuilders: FormBuilder,
    private permService: PermissionsService) { }

  ngOnInit(): void {
    this.initComponent();
    this.searchSubject.pipe(debounceTime(300)).subscribe((searchTerm: string) => {
      this.searchElement = searchTerm;
      this.updateQueryParams({ search: this.searchElement });
    });
    this.addCItyForm = this.formBuilders.group({
      region: [null, [Validators.required]],
      city: [null, [Validators.required, noWhitespaceOrNumValidator(),
      Validators.pattern(/^[a-zA-Z0-9\s-]*$/)]]
    });
    this.editCityForm = this.formBuilders.group({
      id: [null],
      region: [null, [Validators.required]],
      city: [null, [Validators.required, noWhitespaceOrNumValidator(),
      Validators.pattern(/^[a-zA-Z0-9\s-]*$/)]]
    });
  }
  async initComponent() {
  this.loader=true
    await this.getComponentPermissions();
    if (this.permissions.canView) {
      this.getTableData();
    }
  }

  async getComponentPermissions() {
    const url = this.permService.getCurrentUrl();
    // const menuFromLocal = this.permService.loadMenuFromLocalStorage();
    const menuFromLocal = await this.permService.waitForMenuInLocalStorage();

    const matchedMenu = this.permService.findMenuPermissions(menuFromLocal, url);

    this.permissions = matchedMenu
      ? {
        canAdd: !!matchedMenu.canAdd,
        canEdit: !!matchedMenu.canEdit,
        canView: !!matchedMenu.canView,
        canDelete: !!matchedMenu.canDelete
      }
      : { ...DEFAULT_PERMISSIONS };

    this.permissionsReady = true;
  }
  get hasAnyActionPermission(): boolean {
    return (
      this.permissionsReady &&
      (this.permissions.canView ||
        this.permissions.canEdit ||
        this.permissions.canDelete)
    );
  }

  resetForm() {
    this.addCItyForm.reset();
    this.editCityForm.reset();
  }

  openEditPopUp(item: RegionAndCity, contentupdate: any) {
    this.getRegion()
    this.selectedId = item.id;
    this.editCityForm.patchValue({
      id: this.selectedId,
      city: item.name,
      region: item.region_id
    })
    this.modalService.open(contentupdate ,{ centered: true });
  }

  addTariffPopUp(content: any) {
    this.getRegion()
    this.modalService.open(content,{ centered: true });
  }

  onAddCity(): void {
    if (this.addCItyForm.valid) {
      const formData: RegionAndCity = {
        name: this.addCItyForm.value.city.trim(),
        region_id: this.addCItyForm.value.region
      }
      this._utilityService.addRegionCity(formData).pipe(
        catchError(error => {
          if (error.status === 400) {
            this.toaster.error(error.error.message);
            this.resetForm();
          } else {
            this.toaster.error('An error occurred. Please try again later.');
            this.resetForm();
          }
          return throwError(error);
        })).subscribe(response => {
          if (response && response.status === 201) {
            this.getTableData();
            this.addCItyForm.reset();
            Swal.fire({
              position: 'center',
              icon: 'success',
              title: response.message,
              showConfirmButton: false,
              timer: 1000
            })
          }
        })
    }
  }
  onEditCity(): void {
    if (this.editCityForm.valid) {
      Swal.fire({
        title: 'Are you sure you want to update this record?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#FB8C04',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes'
      }).then((result) => {
        if (result.isConfirmed) {
          const formData: RegionAndCity = {
            id: this.editCityForm.value.id,
            name: this.editCityForm.value.city.trim(),
            region_id: this.editCityForm.value.region
          };
          this._utilityService.editRegionCity(formData).pipe(
            catchError(error => {
              if (error.status === 400) {
                this.toaster.error(error.error.message);
                this.resetForm();
              } else {
                this.toaster.error('An error occurred. Please try again later.');
                this.resetForm();
              }
              return throwError(error);
            })
          ).subscribe(response => {
            if (response && response.status === 200) {
              this.getTableData();
              this.resetForm();
              Swal.fire({
                position: 'center',
                icon: 'success',
                title: response.message,
                showConfirmButton: false,
                timer: 1000
              });
            }
          });
        }
      });
    }
  }


  private updateQueryParams(paramsToUpdate: any): void {
    this.latestQueryParams = {
      ...this.latestQueryParams,
      ...paramsToUpdate
    };

    if (this.latestQueryParams.search === '') {
      this.latestQueryParams.search = null;
    }
    if (this.latestQueryParams.page === 1) {
      this.latestQueryParams.page = null;
    }

    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }
    if (this.latestQueryParams.size === 10) {
      this.latestQueryParams.size = null;
    }
    if (this.latestQueryParams.sort === null) {
      delete this.latestQueryParams.sort;
    }

    if (this.latestQueryParams.column === null) {
      delete this.latestQueryParams.column;
    }

    if (this.latestQueryParams.page === 1 || (this.latestQueryParams.page && (this.latestQueryParams.search))) {
      if (this.latestQueryParams.page > 1) {
        this.latestQueryParams.page = paramsToUpdate.page;
        if (this.latestQueryParams.size > 10) {
          this.latestQueryParams.size = paramsToUpdate.size;
        }
      } else {
        this.latestQueryParams.page = null;
      }
    }

    if (!this.next_page && (paramsToUpdate.size || paramsToUpdate.search)) {
      // Only include size or search in the query params
      this.latestQueryParams = {
        size: paramsToUpdate.size || null,
        search: paramsToUpdate.search || null
      };
    }
    this.getTableData();
  }

  private getTableData(): void {
    const name = this.latestQueryParams['search'] || this.searchElement;
    const page = this.latestQueryParams['page'] ? +this.latestQueryParams['page'] : 1;
    const size = this.latestQueryParams['size'] ? +this.latestQueryParams['size'] : 10;
    const sort = this.latestQueryParams['sort'] || null;
    const column = this.latestQueryParams['column'] || null;
    this.getAllCities(name, page, size, sort, column);
  }


  getAllCities(name: string, page: number, size: number, sort: string | null, column: string | null): void {
    this.loader = true;
    this.citiesList = [];
    this.serialNumberArray = [];
    this._utilityService
      .getAllCities(name, page, size, sort, column)
      .subscribe({
        next: (res: any) => {
          if (res && res['status'] === 200) {
            this.citiesList = res.data;
            this.citiesList = res.data.map((tariff: any, index: number) => ({
              ...tariff,
              srNo: (res.current_page - 1) * size + index + 1
            }));
            this.current_page = res.current_page;
            this.next_page = res.next_page;
            this.previous_page = res.previous_page;
            this.total_pages = res.total_pages;
            this.total_items = res.total_records;
            this.enablePagination();
          }
          this.loader = false;
        },
        error: () => {
          this.loader = false;
        }
      });
  }
  public searchData(event: Event): void {
    this.searchElement = (event.target as HTMLInputElement)?.value || '';
    this.searchSubject.next(this.searchElement);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.total_pages) {
      this.updateQueryParams({ page: page });
    }
  }

  navigatePage(direction: string): void {
    if (direction === 'prev' && this.current_page > 1) {
      this.goToPage(this.current_page - 1);
    } else if (direction === 'next' && this.next_page !== null) {
      this.goToPage(this.current_page + 1);
    }
  }

  // Add a method to navigate to the entered page
  navigateToPage(): void {
    if (this.pageToGo >= 1 && this.pageToGo <= this.total_pages) {
      this.goToPage(this.pageToGo);
    }
  }

  public changePageSize(): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.pageSize = this.pageSizeControl.value!;
    this.updateQueryParams({ size: this.pageSize })
  }

  enablePagination() {
    const linkElement = document.getElementById('prevPage');

    if (this.previous_page === null) {
      linkElement.setAttribute('disabled', 'true');
      linkElement.style.pointerEvents = 'none';
      linkElement.style.color = 'gray';
      linkElement.addEventListener('click', (event) => {
        event.preventDefault();
      });
    } else {
      linkElement.removeAttribute('disabled');
      linkElement.style.pointerEvents = 'auto';
      linkElement.style.color = '';
      linkElement.removeEventListener('click', (event) => {
        event.preventDefault();
      });
    }
  }
  deleteCity(id: number) {
    if (id) {
      this._utilityService.deleteRegionAndCity(id).pipe(
        catchError(error => {
          if (error.status === 400) {
            this.toaster.error(error.error.message || 'Bad request');
          } else {
            this.toaster.error('An error occurred. Please try again later.');
          }
          this.resetForm();
          return throwError(() => error);
        })
      ).subscribe(() => {
        // Success path — runs on 200, 204, etc.
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'City deleted successfully',
          showConfirmButton: false,
          timer: 1000
        });
        this.getTableData();
      });
    }
  }
  deleteTariffType(id: number, content: any) {
    this.modalService.open(content, { centered: true }).result.then(
      (result) => {
        if (result === 'confirm') {
          this.deleteCity(id)
        }
      },
      (reason) => {
        console.log('Modal dismissed:', reason);
      }
    );
  }
  getRegion() {
    this.regionLoading = true;
    this._utilityService.getRegionDropDownList().subscribe({
      next: (res) => {
        if (res) {
          this.regionList = res;
        }
        this.regionLoading = false;
      },
      error: () => {
        this.regionLoading = false;
      }
    });
  }
}
